import { CreateCaseInput, useCreateCaseMutation } from "~/operations";
import { useSnackbar } from "notistack";
import { InternalRefetchQueryDescriptor } from "@apollo/client/core/types";
import { getError } from "~/lib/handle-error";
import { getTicketDetailsUrl } from "~/lib/ticketUtils";
import { useScope } from "~/hooks/useScope";
import { useCanCreateCase } from "~/lib/iam/actions/mondoo/policy/ExtendedHub/CreateCase";
import { useCanListCases } from "~/lib/iam/actions/mondoo/policy/ExtendedHub/ListCases";
import { useCanCreate } from "~/lib/iam/actions/mondoo/integrations/IntegrationsManager/Create/hook";
import {
  CreateTicketIntegrationDialog,
  CreateTicketIntegrationDialogPayload,
} from "~/pages/tickets/components/CreateTicketIntegrationDialog/CreateTicketIntegrationDialog";
import { useSearchParams } from "react-router-dom";
import { useDialogs } from "@toolpad/core";
import { useFeatureFlags } from "~/providers/FeatureFlagsProvider";

type UseCasesParams = {
  createCaseRefetchQueries: InternalRefetchQueryDescriptor[];
  onTicketCreate?: () => void;
};

export function useTickets({
  createCaseRefetchQueries,
  onTicketCreate,
}: UseCasesParams) {
  const [searchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();
  const { activeScope, spaceScope } = useScope();
  const hasCreateTicketPermissions = useCanCreateCase(spaceScope);
  const hasListTicketsPermissions = useCanListCases(spaceScope);
  const hasIntegrationCreatePermission = useCanCreate(spaceScope);
  const dialogs = useDialogs();
  const { isFeatureEnabled } = useFeatureFlags();

  const [createCase, { loading }] = useCreateCaseMutation({
    refetchQueries: createCaseRefetchQueries,
  });

  const multiTicketEnabled = isFeatureEnabled("Multi-finding ticketing");

  const handleCreateCase = async ({
    data,
    refetchQueries,
  }: {
    data: CreateCaseInput;
    refetchQueries?: InternalRefetchQueryDescriptor[];
  }) => {
    try {
      const { data: createCaseData } = await createCase({
        variables: {
          input: { ...data, isV2: multiTicketEnabled || data.isV2 },
        },
        refetchQueries: refetchQueries
          ? [...refetchQueries, ...createCaseRefetchQueries]
          : createCaseRefetchQueries,
      });

      const ticketId = createCaseData?.createCase.ticketRefs?.[0]?.ticketId;
      const ticketMrn = createCaseData?.createCase.mrn;
      const ticketDetailsUrl = getTicketDetailsUrl({
        scope: activeScope,
        ticketMrn,
      });

      enqueueSnackbar(`Ticket ${ticketId} successfully created!`, {
        variant: "success",
        persist: true,
        button: {
          label: "OPEN TICKET",
          href: ticketDetailsUrl,
        },
      });
      onTicketCreate?.();
    } catch (error) {
      enqueueSnackbar(`Failed to create the ticket. ${getError(error)}!`, {
        variant: "error",
      });
    }
  };

  const handleCreateTicketingIntegrationModalOpen = () => {
    if (!spaceScope) return;

    const payload: CreateTicketIntegrationDialogPayload = {
      hasIntegrationCreatePermission,
      redirectTo: `${location.pathname}?${searchParams}`,
      space: spaceScope,
    };

    dialogs.open(CreateTicketIntegrationDialog, payload);
  };

  return {
    handleCreateCase,
    handleCreateTicketingIntegrationModalOpen,
    hasCreateTicketPermissions,
    hasListTicketsPermissions,
    hasIntegrationCreatePermission,
    loading,
  };
}
